import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SwapHoriz as SwapIcon,
  AssignmentInd as AssignIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import moment from "moment";
import { useAppTheme } from "../../context/ThemeContext";
import { useGetAuditLogsQuery, AuditLog } from "../../services/audit.service";
import { useGetUsersQuery } from "../../services/users.service";

const PAGE_SIZE = 25;

const ACTION_META: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  created: { label: "Created", color: "#22C55E", icon: <AddIcon fontSize="small" /> },
  updated: { label: "Updated", color: "#3B82F6", icon: <EditIcon fontSize="small" /> },
  deleted: { label: "Deleted", color: "#EF4444", icon: <DeleteIcon fontSize="small" /> },
  status_changed: { label: "Status Changed", color: "#F59E0B", icon: <SwapIcon fontSize="small" /> },
  assigned: { label: "Assigned", color: "#8B5CF6", icon: <AssignIcon fontSize="small" /> },
};

const RESOURCES = ["student", "task", "submission"];

function ActionChip({ action }: { action: string }) {
  const meta = ACTION_META[action] ?? { label: action, color: "#94A3B8", icon: null };
  return (
    <Chip
      icon={meta.icon as any}
      label={meta.label}
      size="small"
      sx={{
        bgcolor: meta.color + "18",
        color: meta.color,
        fontWeight: 600,
        fontSize: "0.72rem",
        "& .MuiChip-icon": { color: meta.color },
      }}
    />
  );
}

function ChangesRow({ changes }: { changes: AuditLog["changes"] }) {
  if (!changes || changes.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
      {changes.map((c, i) => (
        <Chip
          key={i}
          label={`${c.field}: ${String(c.from ?? "—")} → ${String(c.to ?? "—")}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.7rem", height: 22, borderColor: "#CBD5E1", color: "#475569" }}
        />
      ))}
    </Box>
  );
}

function AuditRow({
  log,
  userMap,
  isNewTheme,
}: {
  log: AuditLog;
  userMap: Record<string, string>;
  isNewTheme: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasChanges = log.changes && log.changes.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasChanges ? "pointer" : "default" }}
        onClick={() => hasChanges && setOpen((p) => !p)}>
        <TableCell sx={{ width: 36, pr: 0 }}>
          {hasChanges && (
            <IconButton size="small">
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <ActionChip action={log.action} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500} textTransform="capitalize">
            {log.resource}
          </Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={log.resourceId ?? ""}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
              {log.resourceName ?? log.resourceId ?? "—"}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {log.username ?? userMap[log.userId ?? ""] ?? log.userId ?? "—"}
          </Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={moment(log.createdAt).format("YYYY-MM-DD HH:mm:ss")}>
            <Typography variant="body2" color="text.secondary">
              {moment(log.createdAt).fromNow()}
            </Typography>
          </Tooltip>
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1, pl: 6, pr: 2 }}>
                <ChangesRow changes={log.changes} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AuditLogPage() {
  const { isNewTheme, accentColor } = useAppTheme();
  const [page, setPage] = useState(1);
  const [resource, setResource] = useState("");
  const [userId, setUserId] = useState("");

  const { data, isLoading } = useGetAuditLogsQuery({
    resource: resource || undefined,
    userId: userId || undefined,
    limit: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
  });

  const { data: users = [] } = useGetUsersQuery("");

  const userMap = React.useMemo(() => {
    const m: Record<string, string> = {};
    users.forEach((u: any) => { m[u._id] = u.username; });
    return m;
  }, [users]);

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  const cardSx = isNewTheme
    ? {
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }
    : {};

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={isNewTheme ? 700 : 600}>
            Audit Log
          </Typography>
          {isNewTheme && (
            <Box sx={{ width: 36, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Resource</InputLabel>
          <Select
            label="Resource"
            value={resource}
            onChange={(e) => { setResource(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {RESOURCES.map((r) => (
              <MenuItem key={r} value={r} sx={{ textTransform: "capitalize" }}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>User</InputLabel>
          <Select
            label="User"
            value={userId}
            onChange={(e) => { setUserId(e.target.value); setPage(1); }}>
            <MenuItem value="">All Users</MenuItem>
            {users.map((u: any) => (
              <MenuItem key={u._id} value={u._id}>
                {u.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: isNewTheme ? "#F8FAFC" : undefined }}>
                    <TableCell sx={{ width: 36 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name / ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Performed By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.data?.length ? (
                    data.data.map((log) => (
                      <AuditRow
                        key={log._id}
                        log={log}
                        userMap={userMap}
                        isNewTheme={isNewTheme}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" variant="body2">
                          No audit entries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalPages > 1 && (
            <>
              <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1.5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  size="small"
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {data && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {data.total} total entries
        </Typography>
      )}
    </Box>
  );
}
